import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { Json } from "@/integrations/supabase/types";

export interface Proposal {
  id: string;
  tenant_id: string;
  deal_id: string | null;
  account_id: string | null;
  contact_id: string | null;
  title: string;
  status: "draft" | "sent" | "viewed" | "accepted" | "rejected" | "expired";
  content: Json;
  template_id: string | null;
  valid_until: string | null;
  total_amount: number;
  currency: string;
  sent_at: string | null;
  viewed_at: string | null;
  responded_at: string | null;
  metadata: Json;
  created_at: string;
  updated_at: string;
  accounts?: { name: string } | null;
  contacts?: { first_name: string; last_name: string } | null;
  deals?: { title: string } | null;
}

export interface ProposalTemplate {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  content: Json;
  category: string;
  is_default: boolean;
  metadata: Json;
  created_at: string;
  updated_at: string;
}

export function useProposals() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["proposals", profile?.tenant_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proposals")
        .select("*, accounts(name), contacts(first_name, last_name), deals(title)")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Proposal[];
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useCreateProposal() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (proposal: {
      title: string;
      deal_id?: string | null;
      account_id?: string | null;
      contact_id?: string | null;
      content?: Json;
      total_amount?: number;
      valid_until?: string | null;
    }) => {
      const { data, error } = await supabase
        .from("proposals")
        .insert([{ ...proposal, tenant_id: profile?.tenant_id }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      toast.success("Proposal created successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create proposal: ${error.message}`);
    },
  });
}

export function useUpdateProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string; status?: string; content?: Json; total_amount?: number; sent_at?: string }) => {
      const { data, error } = await supabase
        .from("proposals")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      toast.success("Proposal updated successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to update proposal: ${error.message}`);
    },
  });
}

export function useDeleteProposal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("proposals").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposals"] });
      toast.success("Proposal deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete proposal: ${error.message}`);
    },
  });
}

export function useProposalTemplates() {
  const { profile } = useAuth();

  return useQuery({
    queryKey: ["proposal-templates", profile?.tenant_id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("proposal_templates")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as ProposalTemplate[];
    },
    enabled: !!profile?.tenant_id,
  });
}

export function useCreateProposalTemplate() {
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  return useMutation({
    mutationFn: async (template: { name: string; description?: string | null; content: Json; category?: string }) => {
      const { data, error } = await supabase
        .from("proposal_templates")
        .insert([{ ...template, tenant_id: profile?.tenant_id }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal-templates"] });
      toast.success("Template created successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to create template: ${error.message}`);
    },
  });
}

export function useDeleteProposalTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("proposal_templates").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["proposal-templates"] });
      toast.success("Template deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete template: ${error.message}`);
    },
  });
}
